import { Outlet } from '@tanstack/react-router';
import { useCallback, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';

import { prefersReducedMotion } from '@shigo-ui/ui';

import { NetworkBrandLogo } from '../../../../components/branding/network-brand-logo';
import { NetworkBrandLogoTone } from '../../../../components/branding/network-brand-logo.types';
import { AuthPageAtmosphere } from './AuthPageAtmosphere';
import { AuthStepRecorderContext, type AuthStep } from './authStep';

/**
 * The public auth routes share one mounted shell: the atmosphere, card and logo never remount,
 * so moving between sign-in, dashboard selection and password recovery only swaps the card body
 * (each `AuthPanel` plays its own entrance) while the card springs to the new height.
 */
export function AuthLayout() {
  return (
    <AuthShell>
      <Outlet />
    </AuthShell>
  );
}

export function AuthShell({ children }: { children: ReactNode }) {
  const stepHistory = useRef<{ current: AuthStep | null; previous: AuthStep | null }>({
    current: null,
    previous: null,
  });
  const recordStep = useCallback((step: AuthStep) => {
    const history = stepHistory.current;
    if (history.current !== step) {
      history.previous = history.current;
      history.current = step;
    }
    return history.previous;
  }, []);
  const frameRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useSpringHeight(frameRef, contentRef);

  return (
    <main
      className="relative flex min-h-dvh items-center justify-center overflow-x-hidden bg-[var(--bg-page)] px-5 py-10 text-[var(--text-primary)] sm:px-8"
      data-testid="auth-shell"
    >
      <AuthPageAtmosphere />

      <section
        className="bg-card/90 animate-in fade-in-0 slide-in-from-bottom-2 relative z-10 mx-auto w-full max-w-[420px] rounded-3xl border border-[var(--border-default)] px-6 py-8 shadow-[0_28px_80px_rgba(0,0,0,0.14)] backdrop-blur-xl sm:px-9 sm:py-9"
        data-testid="auth-card"
      >
        <div
          className="absolute inset-x-10 top-0 h-px bg-gradient-to-r from-transparent via-[var(--text-primary)]/20 to-transparent"
          aria-hidden="true"
        />

        <NetworkBrandLogo
          className="mx-auto mb-8 h-11 w-40 object-center"
          height={44}
          tone={NetworkBrandLogoTone.Auto}
        />

        <AuthStepRecorderContext.Provider value={recordStep}>
          <div data-testid="auth-card-body" ref={frameRef}>
            <div ref={contentRef}>{children}</div>
          </div>
        </AuthStepRecorderContext.Provider>
      </section>
    </main>
  );
}

// Critically damped spring with a 0.35s response, the same curve as the `--ease-fluid` token.
const springResponse = 0.35;
const springStiffness = ((2 * Math.PI) / springResponse) ** 2;
const springDamping = (4 * Math.PI) / springResponse;
// Keeps focus rings and the panel's header action visible; only the growing bottom edge clips.
const resizeClip = 'inset(-24px -24px 0 -24px)';
// Covers the card's own entrance (`animate-in`). Content that arrives while the card is still
// entering (e.g. the login form right after sign-out) takes its size at once instead of
// unfolding, so a fresh card always looks like a page refresh.
const cardEntranceMs = 400;

/**
 * Springs the frame from its on-screen height to the content's height whenever the content
 * resizes (a step swap, data replacing a skeleton, an inline error). A resize mid-flight retargets
 * the running spring and keeps its velocity, so back-to-back resizes read as one motion. The card
 * stays centered, so it grows evenly up and down; height is a layout property, which is
 * acceptable only because the card is a small isolated subtree. Reduced motion, an empty frame
 * and the card's own entrance resize at once.
 */
function useSpringHeight(
  frameRef: RefObject<HTMLDivElement | null>,
  contentRef: RefObject<HTMLDivElement | null>,
) {
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const content = contentRef.current;
    if (!frame || !content || typeof ResizeObserver === 'undefined') return;

    const mountedAt = performance.now();
    let height = content.offsetHeight;
    let target = height;
    let velocity = 0;
    let frameId = 0;
    // Timed from the first frame: a frame's timestamp can precede the `performance.now()` that
    // scheduled it, which would give a negative step.
    let lastTime: number | null = null;

    const settle = () => {
      cancelAnimationFrame(frameId);
      frameId = 0;
      lastTime = null;
      height = target;
      velocity = 0;
      frame.style.height = '';
      frame.style.clipPath = '';
    };

    const step = (time: number) => {
      // Cap the step so a backgrounded tab resumes smoothly instead of leaping.
      const delta = lastTime === null ? 1 / 60 : Math.min((time - lastTime) / 1000, 1 / 30);
      lastTime = time;
      velocity += (-springStiffness * (height - target) - springDamping * velocity) * delta;
      height += velocity * delta;
      if (Math.abs(target - height) < 0.5 && Math.abs(velocity) < 5) {
        settle();
        return;
      }
      frame.style.height = `${height.toString()}px`;
      frameId = requestAnimationFrame(step);
    };

    const observer = new ResizeObserver(() => {
      const nextHeight = content.offsetHeight;
      if (nextHeight === target) return;
      target = nextHeight;
      const entering = height < 1 || performance.now() - mountedAt < cardEntranceMs;
      if (entering || prefersReducedMotion()) {
        settle();
        return;
      }
      // Hold the on-screen height in this same frame, before the new content paints.
      frame.style.height = `${height.toString()}px`;
      frame.style.clipPath = resizeClip;
      if (!frameId) frameId = requestAnimationFrame(step);
    });
    observer.observe(content);

    return () => {
      observer.disconnect();
      settle();
    };
  }, [frameRef, contentRef]);
}
